import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Faq, Locale, Testimonial } from '@diana/shared'
import { api, type MediaItem } from '../../lib/api.js'
import {
  ABOUT_PHOTO_KEY,
  aboutPhotoMode,
  type AboutPhotoMode,
  type AboutPhotoSetting,
  type SitePhoto,
} from '../../lib/photos.js'
import { Column, Main, TopTitle } from '../../classroom/Shell.js'
import { Button, ErrorLine, FieldLabel, Skeleton, Tabs } from '../../classroom/ui.js'

/**
 * Strona i treści (Studio-Site.png): what the public site shows that Diana
 * changes herself — the two portraits (moved here from Start), the numbers
 * under the homepage headline, the reviews and the questions. The site's
 * look does not change here; only its content.
 */
export function SitePage() {
  const queryClient = useQueryClient()
  const settings = useQuery({ queryKey: ['studio', 'settings'], queryFn: api.studio.settings })
  const [stats, setStats] = useState<Record<string, string>>({})
  const [saved, setSaved] = useState(false)

  const statsValue = (settings.data?.find((s) => s.key === 'stats' && s.locale === null)?.value ?? {}) as Record<string, number | null>
  useEffect(() => {
    if (settings.data) {
      setStats(Object.fromEntries(STATS.map((s) => [s.key, statsValue[s.key] == null ? '' : String(statsValue[s.key])])))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.data])

  const saveStats = useMutation({
    mutationFn: () =>
      api.studio.setSetting(
        'stats',
        Object.fromEntries(STATS.map((s) => [s.key, stats[s.key]?.trim() ? Number(stats[s.key]) : null])),
      ),
    onSuccess: () => {
      setSaved(true)
      queryClient.invalidateQueries({ queryKey: ['studio', 'settings'] })
      queryClient.invalidateQueries({ queryKey: ['content'] })
    },
  })
  const dirty = STATS.some((s) => (stats[s.key] ?? '') !== (statsValue[s.key] == null ? '' : String(statsValue[s.key])))

  return (
    <Column
      density="studio"
      top={
        <>
          <TopTitle studio>Strona i treści</TopTitle>
          <a href="/" target="_blank" rel="noreferrer" className="ml-auto text-class-gloss font-semibold">
            Podgląd strony ›
          </a>
        </>
      }
      bottom={
        <>
          <span className="hidden text-class-gloss text-class-ink-soft desk:inline">
            {saved && !dirty ? '✓ Zapisane — już jest na stronie.' : 'Po zapisaniu zmiany są od razu na stronie.'}
          </span>
          <Button className="ml-auto" disabled={!dirty || saveStats.isPending} onClick={() => saveStats.mutate()}>
            {saveStats.isPending ? 'Zapisuję…' : 'Zapisz'}
          </Button>
        </>
      }
    >
      <Main density="studio" width={960}>
        <section className="flex flex-col gap-1.5">
          <h1 className="cl-heading m-0">Co widać na stronie</h1>
          <p className="m-0 text-class-ink-soft">Zdjęcia, liczby, opinie i pytania. Wygląd strony się tu nie zmienia.</p>
        </section>

        {settings.isPending ? (
          <Skeleton lines={4} />
        ) : settings.isError ? (
          <ErrorLine>Nie udało się wczytać ustawień.</ErrorLine>
        ) : (
          <>
            <section className="grid gap-10 desk:grid-cols-2">
              <HeroPhoto />
              <AboutPhoto />
            </section>

            <section className="flex flex-col gap-4 border-t border-class-line pt-10">
              <h2 className="cl-subheading m-0">Liczby na stronie głównej</h2>
              <div className="grid grid-cols-2 gap-5 desk:grid-cols-4">
                {STATS.map((s) => (
                  <div key={s.key}>
                    <FieldLabel htmlFor={`stat-${s.key}`}>{s.label}</FieldLabel>
                    <input
                      id={`stat-${s.key}`}
                      inputMode="numeric"
                      placeholder="—"
                      className="cl-input"
                      value={stats[s.key] ?? ''}
                      onChange={(e) => {
                        setSaved(false)
                        setStats({ ...stats, [s.key]: e.target.value.replace(/[^\d]/g, '') })
                      }}
                    />
                  </div>
                ))}
              </div>
              <p className="m-0 text-class-gloss text-class-ink-soft">Puste pole — ta liczba się nie pokazuje. Wszystkie puste — cały pasek znika.</p>
            </section>

            <ReviewsAndQuestions />
          </>
        )}
      </Main>
    </Column>
  )
}

const STATS = [
  { key: 'years', label: 'Lata doświadczenia' },
  { key: 'students', label: 'Uczniowie' },
  { key: 'hours', label: 'Godziny zajęć' },
  { key: 'reviews', label: 'Opinie' },
]

/* ── the two portraits ─────────────────────────────────────────────── */

/** The library, the uploader and the bin both photo blocks share. */
function usePhotos() {
  const queryClient = useQueryClient()
  const settings = useQuery({ queryKey: ['studio', 'settings'], queryFn: api.studio.settings })
  const media = useQuery({ queryKey: ['studio', 'media'], queryFn: api.studio.media })
  const [error, setError] = useState('')
  const read = <T,>(key: string): T => (settings.data?.find((s) => s.key === key && s.locale === null)?.value ?? {}) as T
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['studio', 'settings'] })
    queryClient.invalidateQueries({ queryKey: ['studio', 'media'] })
    queryClient.invalidateQueries({ queryKey: ['content'] })
  }
  const upload = useMutation({
    mutationFn: async ({ file, then }: { file: File; then: (item: MediaItem) => Promise<unknown> }) => {
      const item = await api.studio.uploadMedia(file, 'Diana')
      await then(item)
    },
    onSuccess: () => {
      setError('')
      refresh()
    },
    onError: () => setError('Nie udało się wgrać. JPG, PNG lub WebP, do 8 MB.'),
  })
  /*
   * Deleting a file releases whatever points at it, or the site keeps a URL
   * that 404s. „O mnie” falls back to the homepage photo, not to nothing.
   */
  const remove = useMutation({
    mutationFn: async (item: MediaItem) => {
      if (read<SitePhoto>('hero').mediaId === item.id) await api.studio.setSetting('hero', {})
      if (read<AboutPhotoSetting>(ABOUT_PHOTO_KEY).mediaId === item.id) await api.studio.setSetting(ABOUT_PHOTO_KEY, { mode: 'hero' })
      await api.studio.deleteMedia(item.id)
    },
    onSuccess: refresh,
  })
  return { media, read, refresh, upload, remove, error }
}

function Thumb({ photo }: { photo: { url?: string; alt?: string } }) {
  return photo.url ? (
    <img src={photo.url} alt={photo.alt || 'Diana'} className="h-[140px] w-[112px] shrink-0 rounded-class-md object-cover shadow-[inset_0_0_0_1px_var(--color-class-hairline)]" />
  ) : (
    <span className="flex h-[140px] w-[112px] shrink-0 items-end rounded-class-md bg-gradient-to-b from-class-field-soft to-class-sky-200 p-2 text-class-label text-class-ink-soft">
      zdjęcie
    </span>
  )
}

function UploadButton({ busy, pending, onFile, label = 'Wgraj nowe' }: { busy: boolean; pending: boolean; onFile: (file: File) => void; label?: string }) {
  const input = useRef<HTMLInputElement>(null)
  return (
    <>
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) onFile(file)
          e.target.value = ''
        }}
      />
      <button type="button" onClick={() => input.current?.click()} disabled={busy} className="cl-link text-class-gloss">
        {pending ? 'Wgrywam…' : `+ ${label}`}
      </button>
    </>
  )
}

/** The picture strip: pick one, or remove it from the library. */
function Library({
  items,
  activeId,
  busy,
  onPick,
  onDelete,
}: {
  items: MediaItem[]
  activeId?: string
  busy: boolean
  onPick: (item: MediaItem) => void
  onDelete?: (item: MediaItem) => void
}) {
  if (items.length === 0) return <p className="m-0 text-class-gloss text-class-ink-faint">Jeszcze nic nie wgrałaś.</p>
  return (
    <div className="flex flex-wrap gap-3">
      {items.map((item) => {
        const active = item.id === activeId
        return (
          <div key={item.id} className="group relative">
            <button
              type="button"
              onClick={() => onPick(item)}
              disabled={busy}
              aria-pressed={active}
              className={[
                'block h-[88px] w-[70px] cursor-pointer overflow-hidden rounded-class-sm border-0 p-0',
                active ? 'shadow-[0_0_0_3px_var(--color-class-sky-700)]' : 'shadow-[inset_0_0_0_1px_var(--color-class-hairline)] hover:shadow-[0_0_0_2px_var(--color-class-sky-200)]',
              ].join(' ')}
            >
              <img src={item.url} alt={item.alt} className="h-full w-full object-cover" />
            </button>
            {onDelete && (
              <button
                type="button"
                onClick={() => window.confirm('Usunąć to zdjęcie z biblioteki?') && onDelete(item)}
                disabled={busy}
                aria-label="Usuń zdjęcie"
                className="absolute -top-2 -right-2 hidden h-6 w-6 cursor-pointer items-center justify-center rounded-full border-0 bg-class-ink text-class-label text-class-white group-hover:flex"
              >
                ×
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}

function HeroPhoto() {
  const { media, read, refresh, upload, remove, error } = usePhotos()
  const [picking, setPicking] = useState(false)
  const current = read<SitePhoto>('hero')
  const choose = useMutation({
    mutationFn: (item: MediaItem | null) => api.studio.setSetting('hero', item ? { mediaId: item.id, url: item.url, alt: item.alt } : {}),
    onSuccess: () => {
      refresh()
      setPicking(false)
    },
  })
  const busy = choose.isPending || upload.isPending || remove.isPending
  return (
    <div className="flex flex-col gap-4">
      <h2 className="cl-subheading m-0">Zdjęcie na stronie głównej</h2>
      <div className="flex items-center gap-5">
        <Thumb photo={current} />
        <div className="flex flex-col items-start gap-3">
          <p className="m-0 text-class-gloss text-class-ink-soft">Portret obok nagłówka. Najlepiej pionowe, 4:5.</p>
          <Button variant="secondary" onClick={() => setPicking((p) => !p)} aria-expanded={picking}>
            {picking ? 'Gotowe' : 'Zmień zdjęcie'}
          </Button>
          {current.url && (
            <button type="button" onClick={() => choose.mutate(null)} disabled={busy} className="cl-link-quiet text-class-gloss">
              Usuń
            </button>
          )}
        </div>
      </div>
      {picking && (
        <div className="flex flex-col gap-3 rounded-class-md bg-class-white p-4 shadow-[inset_0_0_0_1px_var(--color-class-hairline)]">
          <Library
            items={media.data ?? []}
            activeId={current.mediaId}
            busy={busy}
            onPick={(item) => choose.mutate(item)}
            onDelete={(item) => remove.mutate(item)}
          />
          <UploadButton
            busy={busy}
            pending={upload.isPending}
            onFile={(file) =>
              upload.mutate({ file, then: (item) => api.studio.setSetting('hero', { mediaId: item.id, url: item.url, alt: item.alt }) })
            }
          />
        </div>
      )}
      {error && <ErrorLine>{error}</ErrorLine>}
    </div>
  )
}

/*
 * „O mnie” offers what Diana would ask for out loud: the same picture as the
 * homepage, a different one, or none. „Ta sama” follows the homepage setting
 * rather than copying it.
 */
const ABOUT_MODES: { value: AboutPhotoMode; label: string }[] = [
  { value: 'hero', label: 'Ta sama co na stronie głównej' },
  { value: 'own', label: 'Inne zdjęcie' },
  { value: 'none', label: 'Bez zdjęcia' },
]

function AboutPhoto() {
  const { media, read, refresh, upload, error } = usePhotos()
  const hero = read<SitePhoto>('hero')
  const current = read<AboutPhotoSetting>(ABOUT_PHOTO_KEY)
  const mode = aboutPhotoMode(current)
  const save = useMutation({
    mutationFn: (value: AboutPhotoSetting) => api.studio.setSetting(ABOUT_PHOTO_KEY, value),
    onSuccess: refresh,
  })
  const busy = save.isPending || upload.isPending
  const setMode = (next: AboutPhotoMode) => save.mutate(next === 'own' ? { ...current, mode: 'own' } : { mode: next })
  const shown = mode === 'hero' ? hero : mode === 'own' ? current : {}

  return (
    <div className="flex flex-col gap-4">
      <h2 className="cl-subheading m-0">Zdjęcie w „O mnie”</h2>
      <div className="flex items-center gap-5">
        <Thumb photo={shown} />
        <div role="radiogroup" aria-label="Zdjęcie w „O mnie”" className="flex flex-col gap-2.5">
          {ABOUT_MODES.map((o) => (
            <label key={o.value} className="flex cursor-pointer items-center gap-3 text-class-gloss">
              <input
                type="radio"
                name="about-photo"
                checked={mode === o.value}
                disabled={busy}
                onChange={() => setMode(o.value)}
                className="h-[18px] w-[18px] accent-[var(--color-class-sky-700)]"
              />
              {o.label}
            </label>
          ))}
        </div>
      </div>
      {mode === 'own' && (
        <div className="flex flex-col gap-3 rounded-class-md bg-class-white p-4 shadow-[inset_0_0_0_1px_var(--color-class-hairline)]">
          <Library
            items={media.data ?? []}
            activeId={current.mediaId}
            busy={busy}
            onPick={(item) => save.mutate({ mode: 'own', mediaId: item.id, url: item.url, alt: item.alt })}
          />
          <UploadButton
            busy={busy}
            pending={upload.isPending}
            onFile={(file) =>
              upload.mutate({
                file,
                then: (item) => api.studio.setSetting(ABOUT_PHOTO_KEY, { mode: 'own', mediaId: item.id, url: item.url, alt: item.alt }),
              })
            }
          />
        </div>
      )}
      {mode === 'hero' && !hero.url && (
        <p className="m-0 text-class-gloss text-class-ink-faint">Najpierw wybierz zdjęcie na stronę główną — albo „Inne zdjęcie”.</p>
      )}
      {error && <ErrorLine>{error}</ErrorLine>}
    </div>
  )
}

/* ── reviews and questions, per language ──────────────────────────────── */

const LOCALES: { value: Locale; label: string }[] = [
  { value: 'pl', label: 'Polski' },
  { value: 'uk', label: 'Українська' },
  { value: 'ru', label: 'Русский' },
]

function ReviewsAndQuestions() {
  const [locale, setLocale] = useState<Locale>('pl')
  const queryClient = useQueryClient()
  const reviews = useQuery({ queryKey: ['studio', 'testimonials'], queryFn: api.studio.testimonials })
  const faqs = useQuery({ queryKey: ['studio', 'faqs'], queryFn: api.studio.faqs })
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['studio', 'testimonials'] })
    queryClient.invalidateQueries({ queryKey: ['studio', 'faqs'] })
    queryClient.invalidateQueries({ queryKey: ['content'] })
  }
  const [editing, setEditing] = useState<string | null>(null)

  const myReviews = (reviews.data ?? []).filter((r) => r.locale === locale).sort((a, b) => a.order - b.order)
  const myFaqs = (faqs.data ?? []).filter((f) => f.locale === locale).sort((a, b) => a.order - b.order)

  const move = useMutation({
    mutationFn: async ({ list, from, to }: { list: Faq[]; from: number; to: number }) => {
      const next = [...list]
      const [it] = next.splice(from, 1)
      next.splice(to, 0, it!)
      for (const [i, f] of next.entries()) if (f.order !== i) await api.studio.updateFaq(f.id, { order: i })
    },
    onSuccess: refresh,
  })

  return (
    <section className="flex flex-col gap-3 border-t border-class-line pt-10">
      <div className="flex flex-wrap items-end gap-x-6 gap-y-2">
        <h2 className="cl-subheading m-0 mr-3">Opinie i pytania</h2>
        <Tabs
          label="Język strony"
          value={locale}
          onChange={(v) => {
            setEditing(null)
            setLocale(v)
          }}
          items={LOCALES}
        />
      </div>

      <span className="mt-3 text-class-label font-semibold text-class-ink-faint">Opinie</span>
      <div className="flex flex-col [&>*]:border-t [&>*]:border-class-line [&>*:last-child]:border-b">
        {myReviews.length === 0 && editing !== 'new-review' && (
          <div className="flex min-h-16 items-center gap-6 px-1">
            <span className="flex-1 text-class-gloss text-class-ink-soft">Jeszcze nie ma opinii. Na stronie głównej pokazują się do czterech.</span>
          </div>
        )}
        {myReviews.map((r) =>
          editing === r.id ? (
            <ReviewForm key={r.id} locale={locale} review={r} onDone={() => (setEditing(null), refresh())} />
          ) : (
            <div key={r.id} className="flex min-h-16 items-center gap-4 px-1 py-2">
              <span className="min-w-0 flex-1 leading-snug">
                <span className="block font-semibold">
                  {r.studentName}
                  <span className="font-normal text-class-ink-faint">{[r.country, r.level].filter(Boolean).map((x) => ` · ${x}`).join('')}</span>
                  {r.featured && <span className="ml-2 text-class-label font-semibold text-class-gold-text">na stronie głównej</span>}
                </span>
                <span className="block truncate text-class-gloss text-class-ink-soft">{r.text}</span>
              </span>
              <button type="button" className="cl-link text-class-gloss" onClick={() => setEditing(r.id)}>
                Edytuj
              </button>
            </div>
          ),
        )}
        {editing === 'new-review' && <ReviewForm locale={locale} order={myReviews.length} onDone={() => (setEditing(null), refresh())} />}
      </div>
      {editing !== 'new-review' && (
        <button type="button" className="cl-link self-start text-class-gloss" onClick={() => setEditing('new-review')}>
          + Dodaj opinię
        </button>
      )}

      <span className="mt-5 text-class-label font-semibold text-class-ink-faint">Pytania</span>
      <ol className="m-0 flex list-none flex-col p-0 [&>*]:border-t [&>*]:border-class-line [&>*:last-child]:border-b">
        {myFaqs.map((f, i) =>
          editing === f.id ? (
            <li key={f.id}>
              <FaqForm locale={locale} faq={f} onDone={() => (setEditing(null), refresh())} />
            </li>
          ) : (
            <li key={f.id} className="flex min-h-14 items-center gap-3.5">
              <span className="flex flex-col">
                <button
                  type="button"
                  aria-label="Wyżej"
                  disabled={i === 0 || move.isPending}
                  onClick={() => move.mutate({ list: myFaqs, from: i, to: i - 1 })}
                  className="h-4 cursor-pointer border-0 bg-transparent p-0 text-[10px] leading-none text-class-ink-faint disabled:opacity-30"
                >
                  ▲
                </button>
                <button
                  type="button"
                  aria-label="Niżej"
                  disabled={i === myFaqs.length - 1 || move.isPending}
                  onClick={() => move.mutate({ list: myFaqs, from: i, to: i + 1 })}
                  className="h-4 cursor-pointer border-0 bg-transparent p-0 text-[10px] leading-none text-class-ink-faint disabled:opacity-30"
                >
                  ▼
                </button>
              </span>
              <span className="flex-1">{f.question}</span>
              <button type="button" className="cl-link text-class-gloss" onClick={() => setEditing(f.id)}>
                Edytuj
              </button>
            </li>
          ),
        )}
        {editing === 'new-faq' && (
          <li>
            <FaqForm locale={locale} order={myFaqs.length} onDone={() => (setEditing(null), refresh())} />
          </li>
        )}
      </ol>
      {editing !== 'new-faq' && (
        <button type="button" className="cl-link self-start text-class-gloss" onClick={() => setEditing('new-faq')}>
          + Dodaj pytanie
        </button>
      )}
    </section>
  )
}

function ReviewForm({ locale, review, order = 0, onDone }: { locale: Locale; review?: Testimonial; order?: number; onDone: () => void }) {
  const [v, setV] = useState({
    studentName: review?.studentName ?? '',
    country: review?.country ?? '',
    level: review?.level ?? '',
    text: review?.text ?? '',
    featured: review?.featured ?? true,
  })
  const save = useMutation({
    mutationFn: () =>
      review
        ? api.studio.updateTestimonial(review.id, v)
        : api.studio.createTestimonial({ ...v, locale, photoId: null, order }),
    onSuccess: onDone,
  })
  const del = useMutation({ mutationFn: () => api.studio.deleteTestimonial(review!.id), onSuccess: onDone })
  return (
    <div className="flex flex-col gap-4 bg-class-white px-4 py-5 shadow-[inset_0_0_0_1px_var(--color-class-hairline)]">
      <div className="grid gap-4 desk:grid-cols-3">
        <div>
          <FieldLabel htmlFor="rv-name">Imię (jak na stronie)</FieldLabel>
          <input id="rv-name" className="cl-input" value={v.studentName} onChange={(e) => setV({ ...v, studentName: e.target.value })} />
        </div>
        <div>
          <FieldLabel htmlFor="rv-country">Kraj</FieldLabel>
          <input id="rv-country" className="cl-input" value={v.country} onChange={(e) => setV({ ...v, country: e.target.value })} />
        </div>
        <div>
          <FieldLabel htmlFor="rv-level">Poziom</FieldLabel>
          <input id="rv-level" className="cl-input" value={v.level} onChange={(e) => setV({ ...v, level: e.target.value })} />
        </div>
      </div>
      <div>
        <FieldLabel htmlFor="rv-text">Opinia</FieldLabel>
        <textarea id="rv-text" rows={4} className="cl-input" value={v.text} onChange={(e) => setV({ ...v, text: e.target.value })} />
      </div>
      <label className="flex items-center gap-2.5 text-class-gloss">
        <input type="checkbox" checked={v.featured} onChange={(e) => setV({ ...v, featured: e.target.checked })} className="h-4 w-4 accent-[var(--color-class-sky-700)]" />
        Na stronie głównej
      </label>
      <div className="flex flex-wrap items-center gap-4">
        <Button variant="secondary" disabled={!v.studentName.trim() || !v.text.trim() || save.isPending} onClick={() => save.mutate()}>
          Zapisz opinię
        </Button>
        <button type="button" className="cl-link-quiet" onClick={onDone}>
          Anuluj
        </button>
        {review && (
          <button type="button" className="cl-link-quiet ml-auto text-class-wrong" onClick={() => window.confirm('Usunąć tę opinię?') && del.mutate()}>
            Usuń
          </button>
        )}
      </div>
      {save.isError && <ErrorLine>Nie udało się zapisać.</ErrorLine>}
    </div>
  )
}

function FaqForm({ locale, faq, order = 0, onDone }: { locale: Locale; faq?: Faq; order?: number; onDone: () => void }) {
  const [v, setV] = useState({ question: faq?.question ?? '', answer: faq?.answer ?? '' })
  const save = useMutation({
    mutationFn: () => (faq ? api.studio.updateFaq(faq.id, v) : api.studio.createFaq({ ...v, locale, order })),
    onSuccess: onDone,
  })
  const del = useMutation({ mutationFn: () => api.studio.deleteFaq(faq!.id), onSuccess: onDone })
  return (
    <div className="flex flex-col gap-4 bg-class-white px-4 py-5 shadow-[inset_0_0_0_1px_var(--color-class-hairline)]">
      <div>
        <FieldLabel htmlFor="faq-q">Pytanie</FieldLabel>
        <input id="faq-q" className="cl-input" value={v.question} onChange={(e) => setV({ ...v, question: e.target.value })} />
      </div>
      <div>
        <FieldLabel htmlFor="faq-a">Odpowiedź</FieldLabel>
        <textarea id="faq-a" rows={4} className="cl-input" value={v.answer} onChange={(e) => setV({ ...v, answer: e.target.value })} />
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <Button variant="secondary" disabled={!v.question.trim() || !v.answer.trim() || save.isPending} onClick={() => save.mutate()}>
          Zapisz pytanie
        </Button>
        <button type="button" className="cl-link-quiet" onClick={onDone}>
          Anuluj
        </button>
        {faq && (
          <button type="button" className="cl-link-quiet ml-auto text-class-wrong" onClick={() => window.confirm('Usunąć to pytanie?') && del.mutate()}>
            Usuń
          </button>
        )}
      </div>
      {save.isError && <ErrorLine>Nie udało się zapisać.</ErrorLine>}
    </div>
  )
}
